/**
 * CoachPlaybook - Dashboard Component
 * Version: 0.6.0
 * Description: Multi-sport dashboard with sport selector and team logo support
 * 
 * v0.5.99 Changes:
 * - Added data-element attributes for UI debugging/feedback
 */

import React, { useState, useEffect } from 'react';
import { getStorageStats, getSetting, getPlayersBySport, getPlaysBySport, getTeamsBySport } from '../../utils/db';
import SportSelector from '../common/SportSelector';
import { useSport } from '../common/SportContext';

const Dashboard = ({ onNavigate, teamName, teams = [], currentTeamId, onTeamSwitch }) => {
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalPlays: 0,
    opponents: 0
  });
  const [teamLogo, setTeamLogo] = useState(null);

  // Sport context
  const { currentSport, currentAgeGroup, setSport, setAgeGroup, sportConfig } = useSport();

  useEffect(() => {
    const loadData = async () => {
      // Load sport-scoped stats
      const [players, plays, teams] = await Promise.all([
        getPlayersBySport(currentSport),
        getPlaysBySport(currentSport),
        getTeamsBySport(currentSport)
      ]);
      setStats({
        totalPlayers: players.length,
        totalPlays: plays.length,
        opponents: teams.length
      });

      // Load team logo
      const logo = await getSetting('teamLogo');
      if (logo) {
        setTeamLogo(logo);
      }
    };
    loadData();
  }, [currentTeamId, currentSport]); // Reload when team or sport changes

  return (
    <div data-element="dashboard-root" className="h-full overflow-y-auto bg-slate-900">
      <div className="p-6 md:p-10 pb-24">
        
        {/* Header Section */}
        <div data-element="dashboard-header" className="mb-10">
          <div className="flex items-center gap-4 mb-2">
            {/* Team Logo or Default Basketball */}
            {teamLogo ? (
              <div data-element="dashboard-header-logo" className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg overflow-hidden bg-slate-800 border-2 border-slate-700">
                <img src={teamLogo} alt="Team Logo" className="w-14 h-14 object-contain" />
              </div>
            ) : (
              <div data-element="dashboard-header-logo-default" className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg" style={sportConfig?.accentColor ? { background: `linear-gradient(135deg, ${sportConfig.accentColor}, ${sportConfig.accentColor}dd)` } : undefined}>
                <span className="text-4xl">{sportConfig?.icon || '🏀'}</span>
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 data-element="dashboard-header-title" className="text-3xl md:text-4xl font-black text-white tracking-tight">
                  {teamName || 'CoachPlaybook'}
                </h1>
                {/* Team Switcher - shown if multiple teams exist */}
                {teams.length > 1 && (
                  <select
                    data-element="dashboard-header-team-switcher"
                    value={currentTeamId || ''}
                    onChange={(e) => onTeamSwitch && onTeamSwitch(e.target.value)}
                    className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <p data-element="dashboard-header-subtitle" className="text-slate-400 text-lg">Multi-Sport Coaching Platform</p>
            </div>
          </div>
        </div>

        {/* Sport Selector */}
        <div data-element="dashboard-sport-selector" className="mb-10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-2xl">{sportConfig?.icon || '🏀'}</span>
            Select Your Sport
          </h3>
          <SportSelector
            selectedSport={currentSport}
            selectedAgeGroup={currentAgeGroup}
            onSportChange={setSport}
            onAgeGroupChange={setAgeGroup}
          />
        </div>

        {/* Main Action Cards - Big & Bold */}
        <div data-element="dashboard-main-cards" className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          
          {/* Team Roster Card */}
          <button
            data-element="dashboard-card-roster"
            onClick={() => onNavigate('roster')}
            className="group relative bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 text-left shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-start justify-between">
              <div>
                <div data-element="dashboard-card-roster-icon" className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-white/30 transition-colors">
                  <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
                <h2 data-element="dashboard-card-roster-title" className="text-3xl md:text-4xl font-black text-white mb-2">
                  Team Roster
                </h2>
                <p data-element="dashboard-card-roster-subtitle" className="text-blue-200 text-lg">
                  Manage players & assignments
                </p>
              </div>
              <div data-element="dashboard-card-roster-stats" className="text-right">
                <div className="text-7xl md:text-8xl font-black text-white/90">
                  {stats.totalPlayers}
                </div>
                <div className="text-blue-200 text-xl font-semibold">
                  {stats.totalPlayers === 1 ? 'Player' : 'Players'}
                </div>
              </div>
            </div>
            <div className="absolute bottom-6 right-6 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          {/* Playbook Card */}
          <button
            data-element="dashboard-card-playbook"
            onClick={() => onNavigate('plays')}
            className="group relative bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-8 text-left shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-start justify-between">
              <div>
                <div data-element="dashboard-card-playbook-icon" className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-white/30 transition-colors">
                  <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                </div>
                <h2 data-element="dashboard-card-playbook-title" className="text-3xl md:text-4xl font-black text-white mb-2">
                  Playbook
                </h2>
                <p data-element="dashboard-card-playbook-subtitle" className="text-emerald-200 text-lg">
                  Design & animate plays
                </p>
              </div>
              <div data-element="dashboard-card-playbook-stats" className="text-right">
                <div className="text-7xl md:text-8xl font-black text-white/90">
                  {stats.totalPlays}
                </div>
                <div className="text-emerald-200 text-xl font-semibold">
                  {stats.totalPlays === 1 ? 'Play' : 'Plays'}
                </div>
              </div>
            </div>
            <div className="absolute bottom-6 right-6 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>

        {/* Secondary Cards Row - Consistent Height */}
        <div data-element="dashboard-secondary-cards" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          
          {/* Stats Card */}
          <button
            data-element="dashboard-card-stats"
            onClick={() => onNavigate('stats')}
            className="group bg-slate-800 hover:bg-slate-700 rounded-2xl p-6 text-left transition-all duration-200 border-2 border-slate-700 hover:border-purple-500 flex flex-col"
          >
            <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
              <svg className="w-8 h-8 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
            </div>
            <h3 data-element="dashboard-card-stats-title" className="text-xl font-bold text-white mb-1">Stats</h3>
            <p className="text-slate-400 text-sm mb-3">Track performance</p>
            <div className="mt-auto">
              <span className="text-xs font-medium text-purple-400 bg-purple-500/10 px-2 py-1 rounded">
                Coming Soon
              </span>
            </div>
          </button>

          {/* Opponents Card */}
          <button
            data-element="dashboard-card-opponents"
            onClick={() => onNavigate('scouting')}
            className="group bg-slate-800 hover:bg-slate-700 rounded-2xl p-6 text-left transition-all duration-200 border-2 border-slate-700 hover:border-red-500 flex flex-col"
          >
            <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-500/30 transition-colors">
              <svg className="w-8 h-8 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            <h3 data-element="dashboard-card-opponents-title" className="text-xl font-bold text-white mb-1">Opponents</h3>
            <p className="text-slate-400 text-sm mb-3">Scout matchups</p>
            <div data-element="dashboard-card-opponents-count" className="mt-auto text-2xl font-black text-red-400">
              {stats.opponents} <span className="text-sm font-medium text-slate-500">teams</span>
            </div>
          </button>

          {/* Backup Card */}
          <button
            data-element="dashboard-card-backup"
            onClick={() => onNavigate('data')}
            className="group bg-slate-800 hover:bg-slate-700 rounded-2xl p-6 text-left transition-all duration-200 border-2 border-slate-700 hover:border-amber-500 flex flex-col"
          >
            <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-500/30 transition-colors">
              <svg className="w-8 h-8 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
              </svg>
            </div>
            <h3 data-element="dashboard-card-backup-title" className="text-xl font-bold text-white mb-1">Backup</h3>
            <p className="text-slate-400 text-sm mb-3">Data management</p>
            <div className="mt-auto">
              <span className="text-xs font-medium text-amber-400 bg-amber-500/10 px-2 py-1 rounded">
                Import/Export
              </span>
            </div>
          </button>

          {/* Team Settings Card */}
          <button
            data-element="dashboard-card-settings"
            onClick={() => onNavigate('teamSettings')}
            className="group bg-slate-800 hover:bg-slate-700 rounded-2xl p-6 text-left transition-all duration-200 border-2 border-slate-700 hover:border-cyan-500 flex flex-col"
          >
            <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition-colors">
              <svg className="w-8 h-8 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
              </svg>
            </div>
            <h3 data-element="dashboard-card-settings-title" className="text-xl font-bold text-white mb-1">Settings</h3>
            <p className="text-slate-400 text-sm mb-3">Team & jerseys</p>
            <div className="mt-auto">
              <span className="text-xs font-medium text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded">
                Customize
              </span>
            </div>
          </button>
        </div>

        {/* Quick Tips Section */}
        <div data-element="dashboard-tips" className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
          <h3 data-element="dashboard-tips-title" className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-2xl">💡</span>
            Quick Tips
          </h3>
          <div data-element="dashboard-tips-grid" className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div data-element="dashboard-tip-1" className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-blue-400">1</span>
              </div>
              <p className="text-slate-400">Start by adding players to your roster with jersey assignments</p>
            </div>
            <div data-element="dashboard-tip-2" className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400">2</span>
              </div>
              <p className="text-slate-400">Create plays by dragging players and drawing movement paths</p>
            </div>
            <div data-element="dashboard-tip-3" className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-purple-400">3</span>
              </div>
              <p className="text-slate-400">Scout opponents by adding their teams and key players</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
